import React, { useMemo } from 'react';
import { getFirestoreDb, firebaseAuthService } from '@/core/services';
import { UserFirestoreDataSource } from '@/features/app/user/data/datasources/remote';
import { UserLocalForageDataSource } from '@/features/app/user/data/datasources/local';
import { UserRepositoryImpl } from '@/features/app/user/data/repositories/user-repository-impl';
import { GetUserByIdUseCase } from '@/features/app/user/domain/usecases/get-user-by-id';
import { GetAllUsersUseCase } from '@/features/app/user/domain/usecases/get-all-users';
import { CreateUserUseCase } from '@/features/app/user/domain/usecases/create-user';
import { UpdateUserUseCase } from '@/features/app/user/domain/usecases/update-user';
import { DeleteUserUseCase } from '@/features/app/user/domain/usecases/delete-user';
import { ReminderSettingsFirestoreDataSource } from '@/features/app/reminder-settings/data/datasources/remote';
import { ReminderSettingsLocalForageDataSource } from '@/features/app/reminder-settings/data/datasources/local';
import { ReminderSettingsRepositoryImpl } from '@/features/app/reminder-settings/data/repositories/reminder-settings-repository-impl';
import { GetUserUidUseCase } from '@/features/authentication/domain/usecases/get-user-uid';
import { GetReminderSettingsByUserIdUseCase } from '@/features/app/reminder-settings/domain/usecases/get-reminder-settings-by-user-id';
import { CreateReminderSettingsUseCase } from '@/features/app/reminder-settings/domain/usecases/create-reminder-settings';
import { UpdateReminderSettingsUseCase } from '@/features/app/reminder-settings/domain/usecases/update-reminder-settings';
import { DeleteReminderSettingsUseCase } from '@/features/app/reminder-settings/domain/usecases/delete-reminder-settings';
import { SendWhatsAppTestMessageUseCase } from '@/features/app/reminder-settings/domain/usecases/send-whatsapp-test-message.usecase';
import { SubscribeMessageTestSendedUseCase } from '@/features/app/reminder-settings/domain/usecases/subscribe-message-test-sended.usecase';
import { AuthProvider } from '@/features/authentication/presentation/context/AuthContext';
import { UserProvider } from '@/features/app/user/presentation/context/UserContext';
import { SubscriptionFirestoreDataSource } from '@/features/app/subscription/data/datasources/remote';
import { SubscriptionRepositoryImpl } from '@/features/app/subscription/data/repositories/subscription-repository-impl';
import { GetSubscriptionByUserIdUseCase } from '@/features/app/subscription/domain/usecases/get-subscription-by-user-id';
import { GetSubscriptionsByUserIdsUseCase } from '@/features/app/subscription/domain/usecases/get-subscriptions-by-user-ids';
import { SubscriptionProvider } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { ReminderSettingsProvider } from '@/features/app/reminder-settings/presentation/context/ReminderSettingsContext';
import { ClientFirestoreDataSource } from '@/features/app/clients/data/datasources/remote';
import { ClientLocalForageDataSource } from '@/features/app/clients/data/datasources/local';
import { ClientRepositoryImpl } from '@/features/app/clients/data/repositories/client-repository-impl';
import { CreateClientUseCase } from '@/features/app/clients/domain/usecases/create-client';
import { GetAllClientsByUserIdUseCase } from '@/features/app/clients/domain/usecases/get-all-clients-by-user-id';
import { GetClientsWithServicesByUserIdUseCase } from '@/features/app/clients/domain/usecases/get-clients-with-services-by-user-id';
import { ReminderFirestoreDataSource } from '@/features/app/reminders/data/datasources/remote';
import { ReminderLocalForageDataSource } from '@/features/app/reminders/data/datasources/local';
import { ReminderRepositoryImpl } from '@/features/app/reminders/data/repositories/reminder-repository-impl';
import { CreateReminderUseCase } from '@/features/app/reminders/domain/usecases/create-reminder';
import { GetAllRemindersUseCase } from '@/features/app/reminders/domain/usecases/get-all-reminders';
import { UpdateReminderUseCase } from '@/features/app/reminders/domain/usecases/update-reminder';
import { ServiceFirestoreDataSource } from '@/features/app/services/data/datasources/remote';
import { ServiceLocalForageDataSource } from '@/features/app/services/data/datasources/local';
import { ServiceRepositoryImpl } from '@/features/app/services/data/repositories/service-repository-impl';
import { GetAllServicesUseCase } from '@/features/app/services/domain/usecases/get-all-services';
import { CreateServiceUseCase } from '@/features/app/services/domain/usecases/create-service';
import { UpdateServiceUseCase } from '@/features/app/services/domain/usecases/update-service';
import { DeleteServiceUseCase } from '@/features/app/services/domain/usecases/delete-service';
import { ClientProvider } from '@/features/app/clients/presentation/context/ClientContext';
import { ReminderProvider } from '@/features/app/reminders/presentation/context/ReminderContext';
import { ServiceProvider } from '@/features/app/services/presentation/context/ServiceContext';
import { AdminAllDataProvider } from '@/features/admin/presentation/context/AdminAllDataContext';
import { PlanFirestoreDataSource } from '@/features/plans/data/datasources/remote';
import { PlanLocalForageDataSource } from '@/features/plans/data/datasources/local';
import { PlanRepositoryImpl } from '@/features/plans/data/repositories/plan-repository-impl';
import { GetAllPlansUseCase } from '@/features/plans/domain/usecases/get-all-plans';
import { GetPlanByIdUseCase } from '@/features/plans/domain/usecases/get-plan-by-id';
import { CreatePlanUseCase } from '@/features/plans/domain/usecases/create-plan';
import { UpdatePlanUseCase } from '@/features/plans/domain/usecases/update-plan';
import { DeletePlanUseCase } from '@/features/plans/domain/usecases/delete-plan';
import { PlansProvider } from '@/features/plans/presentation/context/PlansContext';
import { CreateSubscriptionUseCase } from '@/features/payments/domain/usecases/create-subscription';
import { ActivateFreePlanUseCase } from '@/features/payments/domain/usecases/activate-free-plan';
import { CancelSubscriptionUseCase } from '@/features/payments/domain/usecases/cancel-subscription';
import { PaymentsFirestoreDataSource } from '@/features/billing-invoices/data/datasources/payments-remote';
import { PaymentsLocalForageDataSource } from '@/features/billing-invoices/data/datasources/payments-local';
import { PaymentsRepositoryImpl } from '@/features/billing-invoices/data/repositories/payments-repository-impl';
import { GetPaymentsByUserIdUseCase } from '@/features/billing-invoices/domain/usecases/get-payments-by-user-id';
import { SubscribePaymentDocForUserUseCase } from '@/features/billing-invoices/domain/usecases/subscribe-payment-doc-for-user';
import { BillingInvoicesProvider } from '@/features/billing-invoices/presentation/context/BillingInvoicesContext';
import { PaymentsProvider } from '@/features/payments/presentation/context/PaymentsContext';
import { ToastProvider } from '@/core/presentation/components/Toast';
import { AppContentFirestoreDataSource } from '@/features/app-content/data/datasources/remote';
import { AppContentLocalForageDataSource } from '@/features/app-content/data/datasources/local';
import { AppContentRepositoryImpl } from '@/features/app-content/data/repositories/app-content-repository';
import { GetAppContentUseCase } from '@/features/app-content/domain/usecases/get-app-content.usecase';
import { UpdateAppContentUseCase } from '@/features/app-content/domain/usecases/update-app-content.usecase';
import { InvalidateAppContentCacheUseCase } from '@/features/app-content/domain/usecases/invalidate-app-content-cache.usecase';
import { GetTermsOfUseUseCase } from '@/features/app-content/domain/usecases/get-terms-of-use.usecase';
import { GetPrivacyPolicyUseCase } from '@/features/app-content/domain/usecases/get-privacy-policy.usecase';
import { GetTermsOfUseVersionsUseCase } from '@/features/app-content/domain/usecases/get-terms-of-use-versions.usecase';
import { GetPrivacyPolicyVersionsUseCase } from '@/features/app-content/domain/usecases/get-privacy-policy-versions.usecase';
import { UpdateTermsOfUseUseCase } from '@/features/app-content/domain/usecases/update-terms-of-use.usecase';
import { UpdatePrivacyPolicyUseCase } from '@/features/app-content/domain/usecases/update-privacy-policy.usecase';
import { AppContentProvider } from '@/features/app-content/presentation/context/AppContentContext';

export function CompositionRoot({ children }: { children: React.ReactNode }) {
  const {
    getUserByIdUseCase,
    getAllUsersUseCase,
    createUserUseCase,
    updateUserUseCase,
    deleteUserUseCase,
    getUserUidUseCase,
    getReminderSettingsByUserIdUseCase,
    createReminderSettingsUseCase,
    updateReminderSettingsUseCase,
    deleteReminderSettingsUseCase,
    sendWhatsAppTestMessageUseCase,
    subscribeMessageTestSendedUseCase,
    getAllClientsByUserIdUseCase,
    getClientsWithServicesByUserIdUseCase,
    getAllRemindersUseCase,
    updateReminderUseCase,
    getAllServicesUseCase,
    createServiceUseCase,
    updateServiceUseCase,
    deleteServiceUseCase,
    getAllPlansUseCase,
    getPlanByIdUseCase,
    createPlanUseCase,
    updatePlanUseCase,
    deletePlanUseCase,
    createSubscriptionUseCase,
    activateFreePlanUseCase,
    cancelSubscriptionUseCase,
    getPaymentsByUserIdUseCase,
    subscribePaymentDocForUserUseCase,
    getSubscriptionByUserIdUseCase,
    getSubscriptionsByUserIdsUseCase,
    getTermsOfUseUseCase,
    getPrivacyPolicyUseCase,
    getTermsOfUseVersionsUseCase,
    getPrivacyPolicyVersionsUseCase,
    updateTermsOfUseUseCase,
    updatePrivacyPolicyUseCase,
    invalidateAppContentCacheUseCase,
  } = useMemo(() => {
    const db = getFirestoreDb();

    const userRemote = new UserFirestoreDataSource(db);
    const userLocal = new UserLocalForageDataSource();
    const userRepository = new UserRepositoryImpl(userRemote, userLocal);

    const subscriptionRemote = new SubscriptionFirestoreDataSource(db);
    const subscriptionRepository = new SubscriptionRepositoryImpl(subscriptionRemote);
    const getSubscriptionByUserIdUseCase = new GetSubscriptionByUserIdUseCase(
      subscriptionRepository
    );
    const getSubscriptionsByUserIdsUseCase = new GetSubscriptionsByUserIdsUseCase(
      subscriptionRepository
    );

    const reminderSettingsRemote = new ReminderSettingsFirestoreDataSource(db);
    const reminderSettingsLocal = new ReminderSettingsLocalForageDataSource();
    const reminderSettingsRepository = new ReminderSettingsRepositoryImpl(
      reminderSettingsRemote,
      reminderSettingsLocal
    );

    const createReminderSettingsUseCase = new CreateReminderSettingsUseCase(reminderSettingsRepository);

    const clientRemote = new ClientFirestoreDataSource(db);
    const clientLocal = new ClientLocalForageDataSource();
    const clientRepository = new ClientRepositoryImpl(clientRemote, clientLocal);

    const reminderRemote = new ReminderFirestoreDataSource(db);
    const reminderLocal = new ReminderLocalForageDataSource();
    const reminderRepository = new ReminderRepositoryImpl(reminderRemote, reminderLocal);

    const serviceRemote = new ServiceFirestoreDataSource(db);
    const serviceLocal = new ServiceLocalForageDataSource();
    const serviceRepository = new ServiceRepositoryImpl(serviceRemote, serviceLocal);

    const createClientUseCase = new CreateClientUseCase(clientRepository);
    const createReminderUseCase = new CreateReminderUseCase(reminderRepository);
    const createServiceUseCase = new CreateServiceUseCase(
      serviceRepository,
      createClientUseCase,
      createReminderUseCase
    );

    const getAllServicesUseCase = new GetAllServicesUseCase(serviceRepository);
    const getAllRemindersUseCase = new GetAllRemindersUseCase(reminderRepository);
    const updateReminderUseCase = new UpdateReminderUseCase(reminderRepository);

    const planRemote = new PlanFirestoreDataSource(db);
    const planLocal = new PlanLocalForageDataSource();
    const planRepository = new PlanRepositoryImpl(planRemote, planLocal);

    const paymentsRemote = new PaymentsFirestoreDataSource(db);
    const paymentsLocal = new PaymentsLocalForageDataSource();
    const paymentsRepository = new PaymentsRepositoryImpl(paymentsRemote, paymentsLocal);

    const appContentRemote = new AppContentFirestoreDataSource(db);
    const appContentLocal = new AppContentLocalForageDataSource();
    const appContentRepository = new AppContentRepositoryImpl(appContentRemote, appContentLocal);
    const getAppContentUseCase = new GetAppContentUseCase(appContentRepository);
    const updateAppContentUseCase = new UpdateAppContentUseCase(
      appContentRepository,
      getAppContentUseCase
    );
    const invalidateAppContentCacheUseCase = new InvalidateAppContentCacheUseCase(
      appContentRepository
    );
    const getTermsOfUseUseCase = new GetTermsOfUseUseCase(getAppContentUseCase);
    const getPrivacyPolicyUseCase = new GetPrivacyPolicyUseCase(getAppContentUseCase);
    const getTermsOfUseVersionsUseCase = new GetTermsOfUseVersionsUseCase(getAppContentUseCase);
    const getPrivacyPolicyVersionsUseCase = new GetPrivacyPolicyVersionsUseCase(getAppContentUseCase);
    const updateTermsOfUseUseCase = new UpdateTermsOfUseUseCase(updateAppContentUseCase);
    const updatePrivacyPolicyUseCase = new UpdatePrivacyPolicyUseCase(updateAppContentUseCase);

    return {
      getUserByIdUseCase: new GetUserByIdUseCase(userRepository),
      getAllUsersUseCase: new GetAllUsersUseCase(userRepository),
      createUserUseCase: new CreateUserUseCase(
        firebaseAuthService,
        userRepository,
        createReminderSettingsUseCase
      ),
      updateUserUseCase: new UpdateUserUseCase(userRepository),
      deleteUserUseCase: new DeleteUserUseCase(userRepository),
      getUserUidUseCase: new GetUserUidUseCase(userRepository),
      getReminderSettingsByUserIdUseCase: new GetReminderSettingsByUserIdUseCase(
        reminderSettingsRepository
      ),
      createReminderSettingsUseCase,
      updateReminderSettingsUseCase: new UpdateReminderSettingsUseCase(reminderSettingsRepository),
      deleteReminderSettingsUseCase: new DeleteReminderSettingsUseCase(reminderSettingsRepository),
      sendWhatsAppTestMessageUseCase: new SendWhatsAppTestMessageUseCase(),
      subscribeMessageTestSendedUseCase: new SubscribeMessageTestSendedUseCase(
        reminderSettingsRepository
      ),
      getAllClientsByUserIdUseCase: new GetAllClientsByUserIdUseCase(clientRepository),
      getClientsWithServicesByUserIdUseCase: new GetClientsWithServicesByUserIdUseCase(
        new GetAllClientsByUserIdUseCase(clientRepository),
        getAllServicesUseCase
      ),
      getAllRemindersUseCase,
      updateReminderUseCase,
      getAllServicesUseCase,
      createServiceUseCase,
      updateServiceUseCase: new UpdateServiceUseCase(serviceRepository, reminderRepository),
      deleteServiceUseCase: new DeleteServiceUseCase(serviceRepository),
      getAllPlansUseCase: new GetAllPlansUseCase(planRepository),
      getPlanByIdUseCase: new GetPlanByIdUseCase(planRepository),
      createPlanUseCase: new CreatePlanUseCase(planRepository),
      updatePlanUseCase: new UpdatePlanUseCase(planRepository),
      deletePlanUseCase: new DeletePlanUseCase(planRepository),
      createSubscriptionUseCase: new CreateSubscriptionUseCase(),
      activateFreePlanUseCase: new ActivateFreePlanUseCase(),
      cancelSubscriptionUseCase: new CancelSubscriptionUseCase(),
      getPaymentsByUserIdUseCase: new GetPaymentsByUserIdUseCase(paymentsRepository),
      subscribePaymentDocForUserUseCase: new SubscribePaymentDocForUserUseCase(
        paymentsRepository
      ),
      getSubscriptionByUserIdUseCase,
      getSubscriptionsByUserIdsUseCase,
      getTermsOfUseUseCase,
      getPrivacyPolicyUseCase,
      getTermsOfUseVersionsUseCase,
      getPrivacyPolicyVersionsUseCase,
      updateTermsOfUseUseCase,
      updatePrivacyPolicyUseCase,
      invalidateAppContentCacheUseCase,
    };
  }, []);

  return (
    <AuthProvider>
      <PlansProvider
        getAllPlansUseCase={getAllPlansUseCase}
        getPlanByIdUseCase={getPlanByIdUseCase}
        createPlanUseCase={createPlanUseCase}
        updatePlanUseCase={updatePlanUseCase}
        deletePlanUseCase={deletePlanUseCase}
      >
      <PaymentsProvider
        createSubscriptionUseCase={createSubscriptionUseCase}
        activateFreePlanUseCase={activateFreePlanUseCase}
        cancelSubscriptionUseCase={cancelSubscriptionUseCase}
      >
      <UserProvider
        getUserByIdUseCase={getUserByIdUseCase}
        getAllUsersUseCase={getAllUsersUseCase}
        createUserUseCase={createUserUseCase}
        updateUserUseCase={updateUserUseCase}
        deleteUserUseCase={deleteUserUseCase}
      >
        <SubscriptionProvider
          getUserUidUseCase={getUserUidUseCase}
          getSubscriptionByUserIdUseCase={getSubscriptionByUserIdUseCase}
          getSubscriptionsByUserIdsUseCase={getSubscriptionsByUserIdsUseCase}
        >
        <BillingInvoicesProvider
          getPaymentsByUserIdUseCase={getPaymentsByUserIdUseCase}
          subscribePaymentDocForUserUseCase={subscribePaymentDocForUserUseCase}
        >
        <ReminderSettingsProvider
          getUserUidUseCase={getUserUidUseCase}
          getReminderSettingsByUserIdUseCase={getReminderSettingsByUserIdUseCase}
          createReminderSettingsUseCase={createReminderSettingsUseCase}
          updateReminderSettingsUseCase={updateReminderSettingsUseCase}
          deleteReminderSettingsUseCase={deleteReminderSettingsUseCase}
          sendWhatsAppTestMessageUseCase={sendWhatsAppTestMessageUseCase}
          subscribeMessageTestSendedUseCase={subscribeMessageTestSendedUseCase}
        >
          <ClientProvider
            getUserUidUseCase={getUserUidUseCase}
            getClientsWithServicesByUserIdUseCase={getClientsWithServicesByUserIdUseCase}
          >
          <ReminderProvider
            getUserUidUseCase={getUserUidUseCase}
            getAllRemindersUseCase={getAllRemindersUseCase}
            updateReminderUseCase={updateReminderUseCase}
          >
          <ServiceProvider
            getUserUidUseCase={getUserUidUseCase}
            getAllServicesUseCase={getAllServicesUseCase}
            createServiceUseCase={createServiceUseCase}
            updateServiceUseCase={updateServiceUseCase}
            deleteServiceUseCase={deleteServiceUseCase}
          >
            <ToastProvider>
              <AppContentProvider
                getTermsOfUseUseCase={getTermsOfUseUseCase}
                getPrivacyPolicyUseCase={getPrivacyPolicyUseCase}
                getTermsOfUseVersionsUseCase={getTermsOfUseVersionsUseCase}
                getPrivacyPolicyVersionsUseCase={getPrivacyPolicyVersionsUseCase}
                updateTermsOfUseUseCase={updateTermsOfUseUseCase}
                updatePrivacyPolicyUseCase={updatePrivacyPolicyUseCase}
                invalidateAppContentCacheUseCase={invalidateAppContentCacheUseCase}
              >
                <AdminAllDataProvider>{children}</AdminAllDataProvider>
              </AppContentProvider>
            </ToastProvider>
          </ServiceProvider>
          </ReminderProvider>
          </ClientProvider>
        </ReminderSettingsProvider>
        </BillingInvoicesProvider>
        </SubscriptionProvider>
      </UserProvider>
      </PaymentsProvider>
      </PlansProvider>
    </AuthProvider>
  );
}
